/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api, isApiError } from '../../api';
import type { Tld, TldDomain } from '../../api/areas/registry';
import { DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { FactRows, TwoColumn } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ObjectTabs } from '../../components/ObjectTabs';
import { Amount } from '../commerce/shared';
import { PoliciesPage } from '../governance/PoliciesPage';
import { TldChannelsPage } from '../integrations/TldChannelsPage';
import { TldTermsPanel } from '../services/TldSettingsPage';
import { TldPricingPanel } from './TldPricing';
import { PublicationText, publisherOf, ReadinessBadge, TldReadinessPanel } from './readiness';
import { VnZoneRuleSection } from '../declarations/VnZones';
import { LIFECYCLE, StatusBadge, useStatusLabel } from '../domains/status';
import { EnumBadge, enumLabel, Flag, REGISTRY_STATUS, RefreshButton, RegistryRef, useRegistryCrumbs, When } from './shared';

/** Key facts of one TLD at a glance: its channel, current costs and add-ons (each links to its tab). */
function TldSummary({ tld }: { tld: Tld }) {
  const { t } = useTranslation('registry');
  const { t: ti } = useTranslation('integrations');
  const base = `/registry/tlds/${encodeURIComponent(tld.id)}`;
  const channel = useApi((signal) => api.getTldChannel(tld.id, { signal }), [tld.id]);
  const pricing = useApi((signal) => api.getPricing({ history: false }, { signal }), []);
  const addons = useApi((signal) => api.listTldAddons(tld.id, { signal }), [tld.id]);
  const now = new Date().toISOString();
  const current = channel.status === 'success' ? channel.data.current : undefined;
  const costs =
    pricing.status === 'success'
      ? pricing.data.registry_prices.filter((p) => p.tld_id === tld.id && p.operation !== 'ADDON' && p.effective_from <= now && (p.effective_until === null || p.effective_until > now) && (p.operation === 'REGISTER' || p.operation === 'RENEW'))
      : [];
  const offered = addons.status === 'success' ? addons.data.items.filter((a) => a.mode) : [];
  const mandatory = offered.filter((a) => a.mode === 'MANDATORY');
  const missing = offered.filter((a) => a.missing_price);
  return (
    <StatGrid className="xl:grid-cols-4">
      <StatCard label={t('tldPage.domains')} value={formatCount(tld.managed_domains)} tone="info" />
      <StatCard
        label={t('tldPage.channel')}
        value={<span className="text-base">{current ? current.connection.name : channel.status === 'loading' ? '…' : '—'}</span>}
        tone={current && !current.connection_ready ? 'warn' : 'neutral'}
        hint={current ? ti(`channel.${current.channel}`) : undefined}
        to={`${base}?tab=channels`}
        linkLabel={t('tldPage.tab.channels')}
      />
      <StatCard
        label={t('tldPage.costs')}
        value={
          costs.length ? (
            <span className="grid gap-0.5 text-base">
              {costs.map((c) => (
                <span key={c.id} className="flex items-baseline gap-2">
                  <span className="text-xs font-normal text-muted-foreground">{c.operation === 'REGISTER' ? t('tldPage.op.REGISTER') : t('tldPage.op.RENEW')}</span>
                  <Amount amount={c.amount} currency={c.currency} />
                </span>
              ))}
            </span>
          ) : pricing.status === 'loading' ? (
            '…'
          ) : (
            t('tldPage.noCost')
          )
        }
        tone={costs.length || pricing.status === 'loading' ? 'neutral' : 'warn'}
        to={`${base}?tab=pricing`}
        linkLabel={t('tldPage.tab.pricing')}
      />
      <StatCard
        label={t('tldPage.addons')}
        value={addons.status === 'success' ? formatCount(offered.length) : '…'}
        tone={missing.length ? 'crit' : 'neutral'}
        hint={missing.length ? t('tldPage.addonsMissing', { count: missing.length }) : t('tldPage.addonsMandatory', { count: mandatory.length })}
        to={`${base}?tab=pricing`}
        linkLabel={t('tldPage.tab.pricing')}
      />
    </StatGrid>
  );
}

/**
 * `/registry/tlds/:id`: one zone, and only that zone on every tab: its rules and names
 * (spec §25), registration channel, lifecycle and transfer / restore terms (per TLD, versioned),
 * prices & services (registry cost, fees and add-ons, selling prices: one tab), and the
 * policies that apply to it.
 */
export function TldDetailPage() {
  const { t } = useTranslation('registry');
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  // The old "Dịch vụ" tab is part of "Giá & dịch vụ" now: old links land there.
  useEffect(() => {
    if (params.get('tab') === 'services') {
      const next = new URLSearchParams(params);
      next.set('tab', 'pricing');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);
  const result = useApi((signal) => api.getTld(id, { signal }), [id]);
  const lifecycleLabel = useStatusLabel();
  const zone = result.status === 'success' ? `.${result.data.tld.zone}` : '';
  useDocumentTitle(zone || t('tlds.title'));
  const crumbs = useRegistryCrumbs({ label: t('tlds.title'), to: '/registry/tlds' }, { label: zone || t('tlds.zone') });

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('tlds.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('tlds.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('tldPage.notFound') : t('tldPage.loadError')} />
      </>
    );
  }

  const { tld, lifecycle_counts, recent_domains } = result.data;
  const states = Object.entries(lifecycle_counts).sort((a, b) => b[1] - a[1]);
  const columns: Column<TldDomain>[] = [
    { key: 'name', header: t('tldPage.domain'), cell: (d) => <DomainLink keyOrName={d.ascii_name} label={d.unicode_name !== d.ascii_name ? d.unicode_name : undefined} /> },
    { key: 'state', header: t('tldPage.state'), cell: (d) => <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} /> },
    { key: 'registered', header: t('tldPage.registered'), cell: (d) => <When at={d.registry_created_at} /> },
    { key: 'expires', header: t('tldPage.expires'), cell: (d) => (d.registry_expires_at ? formatDate(d.registry_expires_at) : '—') },
    { key: 'added', header: t('tldPage.added'), cell: (d) => <When at={d.created_at} /> },
  ];

  const info = (
    <div className="space-y-6">
      <TldSummary tld={tld} />
      <TwoColumn
        main={
          <>
            <TldReadinessPanel tldId={tld.id} zone={tld.zone} onChanged={result.reload} />
            {tld.tld_class === 'VN' ? <VnZoneRuleSection zone={tld.zone} /> : null}
            <Section title={t('tldPage.byState')}>
              {states.length === 0 ? (
                <EmptyState title={t('tldPage.noByState')} />
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {states.map(([state, n]) => (
                    <li key={state} className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm">
                      <StatusBadge map={LIFECYCLE} value={state} />
                      <span className="tabular font-semibold">{formatCount(n)}</span>
                      <span className="sr-only">{lifecycleLabel(LIFECYCLE, state)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section flush title={t('tldPage.recent')}>
              <DataTable columns={columns} rows={recent_domains} rowKey={(d) => d.id} empty={<EmptyState title={t('tldPage.noRecent')} />} />
            </Section>
          </>
        }
        side={
          // The zone's profile (reference 18 "Policy profile"): its rules at a glance.
          <Section title={t('tldPage.rules')}>
            <FactRows
              rows={[
                [t('tlds.registry'), <RegistryRef key="r" name={tld.registry_name} code={tld.registry_code} />],
                [t('tlds.class'), <span key="c" title={tld.tld_class}>{enumLabel('tldClass', tld.tld_class)}</span>],
                [t('tlds.dossier'), <span key="d" title={tld.dossier_type}>{enumLabel('dossierType', tld.dossier_type)}</span>],
                [t('tlds.icann'), <Flag key="i" on={tld.icann_governed} yes={t('tlds.governed')} no={t('no')} />],
                [
                  t('tlds.rdapWhois'),
                  <PublicationText
                    key="pub"
                    publisher={tld.readiness?.publisher ?? publisherOf(tld.rdap_enabled, tld.whois_enabled, undefined, tld.tld_class)}
                    connection={tld.readiness?.channel ?? { name: tld.registry_name }}
                  />,
                ],
                [t('tlds.metricGroup'), <span key="g" className="text-xs">{tld.tld_group}</span>],
                [t('tlds.status'), <EnumBadge key="s" group="registryStatus" map={REGISTRY_STATUS} value={tld.status} />],
                [t('tldPage.id'), <CopyText key="id" value={tld.id} />],
              ]}
            />
          </Section>
        }
      />
    </div>
  );
  return (
    <>
      <PageHeader
        title={zone}
        crumbs={crumbs}
        // ID line: class · connection for new registrations · TLD id.
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{enumLabel('tldClass', tld.tld_class)}</span>
            <span aria-hidden="true">·</span>
            <span>{tld.readiness?.channel.name ?? tld.registry_name}</span>
            <span aria-hidden="true">·</span>
            <CopyText value={tld.id} />
          </span>
        }
        badges={
          <>
            <EnumBadge group="registryStatus" map={REGISTRY_STATUS} value={tld.status} />
            <ReadinessBadge summary={tld.readiness} />
            <PublicationText
              publisher={tld.readiness?.publisher ?? publisherOf(tld.rdap_enabled, tld.whois_enabled, undefined, tld.tld_class)}
              connection={tld.readiness?.channel ?? { name: tld.registry_name }}
            />
          </>
        }
        actions={<RefreshButton onClick={result.reload} />}
      />
      <ObjectTabs
        base={`/registry/tlds/${encodeURIComponent(id)}`}
        label={zone}
        tabs={[
          { id: 'info', label: t('tldPage.tab.info'), content: info },
          // `tld_id` narrows the channel list to exactly this TLD (a zone prefix would also match com.vn for com).
          { id: 'channels', label: t('tldPage.tab.channels'), embedded: true, fixed: { tld_id: tld.id }, content: <TldChannelsPage /> },
          { id: 'lifecycle', label: t('tldPage.tab.lifecycle'), embedded: true, content: <TldTermsPanel tldId={tld.id} zone={tld.zone} /> },
          // Prices and add-ons of the TLD in one tab (the old `?tab=services` redirects here).
          { id: 'pricing', label: t('tldPage.tab.pricing'), embedded: true, content: <TldPricingPanel tld={tld} /> },
          { id: 'policies', label: t('tldPage.tab.policies'), embedded: true, content: <PoliciesPage tld={{ id: tld.id, zone: tld.zone }} /> },
        ]}
      />
    </>
  );
}
